import { getLicenseBaseUrl } from '../api.js'
import type {
	MebLoginRequest,
	MebLoginRawResponse,
	MebLoginResult,
} from './dto.js'

/**
 * MEB 業務碼：IP 在白名單內但帳號尚未綁定 2FA；此時 err=true 但 data.token 仍是可用的暫時 token
 * - 出處：service-license-api 的 `origin/feature#1422-ha` 分支 `app/Services/v1/admin/AdminUserService.php:89-95`
 */
const MEB_ERRNO_WHITELIST_NOT_BOUND = 1036

/**
 * MEB licence 登入（POST {getLicenseBaseUrl}/api/v1/admin/login）
 * - 網域由 build mode 決定（getLicenseBaseUrl），不經 v1HttpClient，直接打 MEB licence 服務
 * - 連線失敗 / HTTP 非 2xx / 業務錯誤（err=true）皆丟出帶提示訊息的 Error，交由呼叫端攔截；
 *   唯一例外是 errno=1036（IP 白名單但未綁 2FA），回傳暫時 token 並標示需要綁定
 * @param req MEB 帳號密碼
 * @returns 解析後的 token、是否需要 2FA 綁定、是否可略過 2FA
 */
export const mebLogin = async (
	req: MebLoginRequest,
): Promise<MebLoginResult> => {
	// 打 MEB licence 登入端點，連線層失敗轉成友善訊息
	let res: Response
	try {
		res = await fetch(`${getLicenseBaseUrl()}/api/v1/admin/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(req),
		})
	} catch {
		throw new Error('連線失敗，請稍後再試')
	}

	// HTTP 失敗直接丟出
	if (!res.ok) {
		throw new Error(`伺服器回應錯誤 ${res.status}`)
	}

	// 解析回應外層
	const body = (await res.json()) as MebLoginRawResponse

	// IP 白名單但尚未綁定 2FA：data.token 仍是暫時 token，直接導向綁定流程
	if (
		body.err &&
		body.errno === MEB_ERRNO_WHITELIST_NOT_BOUND &&
		body.data?.token
	) {
		return {
			token: body.data.token,
			needsTwoFaSetup: true,
			allowPass2FA: false,
		}
	}

	// 其餘業務錯誤丟出
	if (body.err) {
		throw new Error(`登入失敗：${body.msg}`)
	}

	// enableOTP !== 1 一律視為尚未綁定：/2fa 頁面顯示 QRCode 綁定畫面
	const needsTwoFaSetup = body.data.enableOTP !== 1
	// allowPass2FA 只有布林 true 代表可略過 2FA
	const allowPass2FA = body.data.allowPass2FA === true

	// 回傳 token、是否需要 2FA 綁定、是否可略過 2FA
	return { token: body.data.token, needsTwoFaSetup, allowPass2FA }
}
